import { ComponentType } from '@angular/cdk/overlay/index';
import { ComponentRef, Injectable, InputSignal, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BootstrapDialogAction } from '.';
import { BootstrapMessageDialog, BootstrapMessageDialogData } from './ui/message/bootstrap-message.dialog';

type size = 'sm' | 'lg' | 'xl' | '';

interface BootstrapDialogOptions {
	size?: size;
	container?: string;
	centered?: boolean;
	scrollable?: boolean;
	windowClass?: string;
}


/**
 * Component to render within the body of a dialog.
 */
export interface BootstrapDialogComponent<C extends { readonly data: InputSignal<D> }, D> extends ComponentType<C> {
}


@Injectable({ providedIn: 'root' })
export class BootstrapDialogService {

	private ngbModalService = inject(NgbModal);



	/**
	*  Create and show a custom dialog.
	*/
	public async showMobileCustom<C extends { readonly data: InputSignal<D> }, D>(component: BootstrapDialogComponent<C, D>, data: D, containerId?: string): Promise<BootstrapDialogAction | undefined> {
		return await this.openDialog(component, data, {
			container: containerId ?? '#dialog-container-layout-part',
			windowClass: 'eforall-mobile-modal',
		});
	}


	/**
	*  Create and show a custom dialog.
	*/
	public async showDashboardCustom<C extends { readonly data: InputSignal<D> }, D>(component: BootstrapDialogComponent<C, D>, data: D, size?: 'sm' | 'lg' | 'xl'): Promise<BootstrapDialogAction | undefined> {
		return await this.openDialog(component, data, {
			size: size ?? '',
			windowClass: 'eforall-modal',
		});
	}


	/**
	 *  Create and show a message dialog with one button to close it.
	 */
	public async showMessage(message: string, buttonText?: string, bullets?: string[]): Promise<BootstrapDialogAction | undefined> {
		return await this.openDialog<BootstrapMessageDialog, BootstrapMessageDialogData>(BootstrapMessageDialog, { message, buttonText, bullets: bullets || [] });
	}


	private async openDialog<C extends { readonly data: InputSignal<D> }, D>(component: BootstrapDialogComponent<C, D>, data: D, options?: BootstrapDialogOptions): Promise<BootstrapDialogAction | undefined> {

		const ref = this.ngbModalService.open(component, this.getDefaultOptions(options));

		/**
		 * This is a hack to get the ComponentRef because NgbModalRef does not expose it.
		 * See https://github.com/ng-bootstrap/ng-bootstrap/issues/4688
		 */
		const componentRef: ComponentRef<C> = ref['_contentRef'].componentRef;
		componentRef.setInput('data', data);

		const result: BootstrapDialogAction | undefined = await ref.result;

		return result;
	}

	/**
	 * Close any open dialogs.
	 */
	closeAll() {
		this.ngbModalService.dismissAll();
	}

	private getDefaultOptions(data: BootstrapDialogOptions | undefined): BootstrapDialogOptions {
		return {
			size: data?.size ?? '',
			container: data?.container,
			centered: data?.centered ?? true,
			scrollable: data?.scrollable ?? true,
			windowClass: data?.windowClass,
		};
	}
}
